import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTeamAuth } from "@/lib/teamAuth";
import { ArrowUpRight, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function TeamResetPassword() {
    const { member, loading, resetPassword } = useTeamAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (member) return <Navigate to="/team" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            toast.error("Missing token");
            return;
        }
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setBusy(true);
        try {
            await resetPassword(token, password);
            toast.success("Password updated.");
            navigate("/team", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Reset failed";
            toast.error(typeof msg === "string" ? msg : "Reset failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="team-reset-page"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 grain"
        >
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-12">
                    <img src="/logo.png" alt="Aximo Studios" className="h-12 w-auto" />
                </Link>
                <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-6">
                    <KeyRound className="w-3.5 h-3.5" />
                    Reset password
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1] mb-10">
                    New
                    <br />
                    <span className="font-serif-i italic text-ember">password.</span>
                </h1>
                <form
                    onSubmit={onSubmit}
                    className="space-y-6 border border-white/10 bg-ink-800/40 p-8"
                    data-testid="team-reset-form"
                >
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                            New password
                        </span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            data-testid="team-reset-password"
                            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight"
                            autoFocus
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                            Confirm
                        </span>
                        <input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Re-enter it"
                            data-testid="team-reset-confirm"
                            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="team-reset-submit"
                        className="btn-ember w-full inline-flex items-center justify-between h-12 px-5 text-sm font-medium tracking-wide disabled:opacity-60"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </main>
    );
}
